import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { CircleNotch, Fire, Snowflake, Thermometer } from "@phosphor-icons/react";
import useDrawsUpdated from "../hooks/useDrawsUpdated";

const API = process.env.REACT_APP_BACKEND_URL;

const CLASS_META = {
  Sizzling: { icon: Fire, bg: "bg-red-500", text: "text-white", light: "bg-red-50 text-red-700 border-red-200", tag: "bg-red-100 text-red-700", letter: "S", desc: "≥ 5 times" },
  Mild:     { icon: Thermometer, bg: "bg-amber-500", text: "text-white", light: "bg-amber-50 text-amber-700 border-amber-200", tag: "bg-amber-100 text-amber-700", letter: "M", desc: "3-4 times" },
  Cold:     { icon: Snowflake, bg: "bg-blue-500", text: "text-white", light: "bg-blue-50 text-blue-700 border-blue-200", tag: "bg-blue-100 text-blue-700", letter: "C", desc: "0-2 times" },
};

export default function ClassMixLast200() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      const res = await axios.get(`${API}/api/analysis/class-mix-last200`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.message || "Failed");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(false); }, [fetchData]);

  // Auto-refresh when a new draw is added/edited/deleted
  useDrawsUpdated(() => fetchData(true));

  if (loading) return <div className="flex items-center justify-center py-16"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;
  if (error || !data || data.error) return <div className="p-6 text-center text-red-600">Failed to load: {error || data?.error || 'No data'}</div>;

  const recentDraws = [...data.draw_compositions].slice(-25).reverse();

  return (
    <div className="space-y-6" data-testid="last200-content">
      {/* Header summary */}
      <div className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white rounded-lg p-5" data-testid="l200-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              Last 200 Numbers — Class Window
              <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-500 text-white px-2 py-0.5 rounded-full font-normal">
                <span className={`w-1.5 h-1.5 rounded-full bg-white ${refreshing ? '' : 'animate-pulse'}`} /> {refreshing ? 'Refreshing…' : 'Auto-updating'}
              </span>
            </h2>
            <p className="text-[12px] text-white/80 mt-1">
              Window: draws <span className="font-mono font-bold">#{data.window_range.from_draw}</span> ({data.window_range.from_date})
              &nbsp;→&nbsp;
              <span className="font-mono font-bold">#{data.window_range.to_draw}</span> ({data.window_range.to_date}) &nbsp;·&nbsp;
              <span className="font-bold">{data.window_range.draws_used}</span> draws × 5 balls = <span className="font-bold">{data.window_range.slots}</span> number slots
            </p>
            <p className="text-[11px] text-white/70 mt-1">
              Average expected per number = <span className="font-mono font-bold">4</span>. Actual distribution below shows which numbers over-indexed (Sizzling) or under-indexed (Cold).
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {["Sizzling", "Mild", "Cold"].map(cls => {
              const m = CLASS_META[cls];
              const Icon = m.icon;
              return (
                <div key={cls} className="bg-white/10 backdrop-blur rounded-lg px-3 py-2 border border-white/20 min-w-[110px]" data-testid={`l200-legend-${cls}`}>
                  <div className="flex items-center gap-1.5">
                    <Icon size={16} weight="fill" />
                    <span className="font-bold text-sm">{cls}</span>
                  </div>
                  <div className="text-[10px] text-white/70">{m.desc}</div>
                  <div className="text-2xl font-bold font-mono mt-0.5">{data.class_summary[cls]?.count ?? 0}</div>
                  <div className="text-[10px] text-white/70">numbers</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Number grid by class */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="l200-class-grid">
        {["Sizzling", "Mild", "Cold"].map(cls => {
          const m = CLASS_META[cls];
          const Icon = m.icon;
          const nums = data.number_data.filter(n => n.class === cls).sort((a, b) => b.times - a.times || a.number - b.number);
          return (
            <div key={cls} className={`border rounded-lg p-4 ${m.light}`} data-testid={`l200-group-${cls}`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon size={18} weight="fill" />
                <span className="font-bold text-sm">{cls}</span>
                <span className="text-[10px] text-gray-500 ml-auto">{nums.length} numbers</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {nums.length === 0 && <span className="text-[11px] italic text-gray-400">— none —</span>}
                {nums.map(n => (
                  <div key={n.number} className="flex flex-col items-center" data-testid={`l200-num-${n.number}`}>
                    <span className={`inline-flex w-9 h-9 rounded-full ${m.bg} ${m.text} items-center justify-center font-mono font-bold text-sm`}>{n.number}</span>
                    <span className="text-[9px] text-gray-600 mt-0.5 font-bold">{n.times}×</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Next-draw expected composition */}
      {data.next_draw_projection && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="l200-next-projection">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white px-5 py-3">
            <h3 className="font-bold text-base">🎯 Next-Draw Expected Composition</h3>
            <p className="text-[11px] text-white/80">Based on last {data.next_draw_projection.recent_draws_used} draws' observed mix and the historical top pattern</p>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border rounded-lg p-3 bg-emerald-50/50">
              <div className="text-[10px] font-bold text-emerald-700 uppercase mb-1">Recent Avg Mix (last {data.next_draw_projection.recent_draws_used})</div>
              <div className="flex gap-2">
                {["Sizzling", "Mild", "Cold"].map(cls => (
                  <div key={cls} className={`flex-1 rounded px-2 py-1.5 text-center ${CLASS_META[cls].tag}`}>
                    <div className="text-[10px] font-bold">{cls}</div>
                    <div className="text-lg font-mono font-bold">{data.next_draw_projection.recent_avg_mix[cls]}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="border rounded-lg p-3 bg-blue-50/50">
              <div className="text-[10px] font-bold text-blue-700 uppercase mb-1">Naive Expected (class-size proportion)</div>
              <div className="flex gap-2">
                {["Sizzling", "Mild", "Cold"].map(cls => (
                  <div key={cls} className={`flex-1 rounded px-2 py-1.5 text-center ${CLASS_META[cls].tag}`}>
                    <div className="text-[10px] font-bold">{cls}</div>
                    <div className="text-lg font-mono font-bold">{data.next_draw_projection.naive_expected_mix[cls]}</div>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-gray-500 mt-1">= 5 × (class size / 50)</div>
            </div>
            <div className="border rounded-lg p-3 bg-violet-50/50">
              <div className="text-[10px] font-bold text-violet-700 uppercase mb-1">Top Historical Pattern</div>
              {data.next_draw_projection.top_historical_pattern ? (
                <>
                  <div className="font-mono font-bold text-xl text-violet-800">{data.next_draw_projection.top_historical_pattern.pattern}</div>
                  <div className="text-[11px] text-violet-700 font-bold mt-1">{data.next_draw_projection.top_historical_pattern.pct}% of {data.window_range.draws_used > 0 ? data.draw_compositions.length : 0} draws</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">S=Sizzling · M=Mild · C=Cold</div>
                </>
              ) : <div className="text-xs text-gray-400">n/a</div>}
            </div>
          </div>
        </div>
      )}

      {/* Top Patterns distribution */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="l200-patterns">
        <div className="bg-[#002FA7] text-white px-5 py-3">
          <h3 className="font-bold text-base">Most Common Class-Mix Patterns (Historical)</h3>
          <p className="text-[11px] text-white/80">Each draw's pattern is computed using its own preceding 200-number window</p>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          {data.top_patterns.map((tp, i) => {
            const parts = tp.pattern.split("-"); // e.g. ["S2", "M3", "C0"]
            return (
              <div key={tp.pattern} className={`border rounded-lg p-3 text-center ${i === 0 ? 'ring-2 ring-[#002FA7] bg-blue-50' : 'bg-gray-50'}`} data-testid={`l200-pattern-${i}`}>
                <div className="flex justify-center gap-1 mb-2">
                  {parts.map((p, pi) => {
                    const cls = p[0] === 'S' ? 'Sizzling' : p[0] === 'M' ? 'Mild' : 'Cold';
                    const num = p.slice(1);
                    return (
                      <span key={pi} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${CLASS_META[cls].tag}`}>
                        {p[0]}{num}
                      </span>
                    );
                  })}
                </div>
                <div className="font-bold text-lg text-gray-800">{tp.count}×</div>
                <div className="text-[10px] text-gray-500">{tp.pct}% of draws</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent draws composition table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="l200-recent-table">
        <div className="bg-gray-800 text-white px-5 py-3">
          <h3 className="font-bold text-base">Recent Draws — Class Composition (Rolling 200-Number Window)</h3>
          <p className="text-[11px] text-white/70">Each draw is classified using the 40 draws immediately preceding it. Newest first.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left text-[10px] uppercase text-gray-600 font-bold">Draw</th>
                {[1,2,3,4,5].map(i => <th key={i} className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Ball {i}</th>)}
                <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Mix</th>
                <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Pattern</th>
              </tr>
            </thead>
            <tbody>
              {recentDraws.map((dc, idx) => (
                <tr key={dc.draw_number} className={idx === 0 ? 'bg-emerald-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`l200-draw-${dc.draw_number}`}>
                  <td className="px-4 py-2 border-b">
                    <div className="font-bold text-gray-800 flex items-center gap-1.5">
                      #{dc.draw_number}
                      {idx === 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500 text-white">LATEST</span>}
                    </div>
                    <div className="text-[10px] text-gray-400">{dc.draw_date}</div>
                  </td>
                  {dc.ball_details.map((bd, bi) => {
                    const m = CLASS_META[bd.class];
                    return (
                      <td key={bi} className="px-3 py-2 border-b text-center">
                        <div className="flex flex-col items-center gap-0.5">
                          <span className={`inline-flex w-9 h-9 rounded-full ${m.bg} ${m.text} items-center justify-center font-mono font-bold text-sm`}>{bd.number}</span>
                          <span className={`text-[9px] font-bold px-1.5 rounded ${m.tag}`}>{m.letter} {bd.times}×</span>
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 border-b text-center">
                    <div className="flex justify-center gap-1">
                      {["Sizzling", "Mild", "Cold"].map(cls => (
                        <span key={cls} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${CLASS_META[cls].tag}`}>{CLASS_META[cls].letter}:{dc.mix[cls]}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 border-b text-center">
                    <span className="font-mono font-bold text-sm text-gray-800">{dc.pattern}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
